import { useState } from 'react';
import { useNotify } from 'react-admin';
import { API_BASE } from '../utils/apiClient';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { CompanionData } from '@garuda/shared';
import { CompanionEditor } from '../components/CompanionEditor';
import SearchIcon from '@mui/icons-material/Search';
import CasinoIcon from '@mui/icons-material/Casino';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

// ─── Types ──────────────────────────────────────────────

interface MovieWithTrivia {
  entityId: string;
  displayName: string;
  releaseYear: number;
  category: string;
}

interface TriviaPreview {
  description?: string;
  progressive_clues?: Array<{ fact: string; weight: number; category: string }>;
  redacted_plot?: { lines?: string[]; redacted_group_of_words?: string[] };
  fact_or_cap?: {
    true_statements?: Array<{ statement: string }>;
    false_statements?: Array<{ statement: string }>;
  };
}

// ─── Constants ──────────────────────────────────────────

const GAME_TYPES = [
  { id: 'guess_the_movie', name: 'Guess the Movie', description: 'Progressive clues reveal a movie', multiMovie: true },
  { id: 'fact_or_cap', name: 'Fact or Cap', description: 'True/false statements about movies', multiMovie: true },
  { id: 'redacted_plot', name: 'Redacted Plot', description: 'Hidden plot words reveal a movie', multiMovie: false },
];

const CATEGORIES = [
  'tamil', 'hindi', 'telugu', 'malayalam', 'kannada',
  'bengali', 'marathi', 'punjabi', 'gujarati', 'odia', 'assamese', 'english',
];

// ─── Component ──────────────────────────────────────────

export const TriviaGameCreate = () => {
  const notify = useNotify();

  // Form state
  const [gameType, setGameType] = useState('guess_the_movie');
  const [category, setCategory] = useState('english');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  // Filters
  const [curatedOnly, setCuratedOnly] = useState(true);

  // Movie selection
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MovieWithTrivia[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedMovies, setSelectedMovies] = useState<MovieWithTrivia[]>([]);

  // Preview
  const [previewData, setPreviewData] = useState<Record<string, TriviaPreview>>({});

  // SEO fields
  const [slug, setSlug] = useState('');
  const [seoDescription, setSeoDescription] = useState('');

  // Companion
  const [companionData, setCompanionData] = useState<Partial<CompanionData> | null>(null);

  // Save
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const gameConfig = GAME_TYPES.find((g) => g.id === gameType)!;

  // ─── Movie Search ─────────────────────────────

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const curatedParam = curatedOnly ? '&curated=true' : '';
      const res = await fetch(
        `${API_BASE}/admin/api/games/trivia/movies?category=${category}&q=${encodeURIComponent(searchQuery)}&limit=10${curatedParam}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Search failed');
      const data = await res.json();
      setSearchResults(data.movies || []);
    } catch {
      notify('Search failed', { type: 'error' });
    } finally {
      setSearching(false);
    }
  };

  const handleRandomMovie = async () => {
    try {
      const curatedParam = curatedOnly ? '&curated=true' : '';
      const res = await fetch(
        `${API_BASE}/admin/api/games/trivia/random?category=${category}${curatedParam}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('No movies found');
      const movie = await res.json();
      addMovie(movie);
    } catch {
      notify('No movies with trivia found for this category', { type: 'warning' });
    }
  };

  const addMovie = (movie: MovieWithTrivia) => {
    if (selectedMovies.find((m) => m.entityId === movie.entityId)) {
      notify('Movie already selected', { type: 'warning' });
      return;
    }
    if (!gameConfig.multiMovie && selectedMovies.length >= 1) {
      notify('This game type only supports 1 movie', { type: 'warning' });
      return;
    }
    setSelectedMovies((prev) => [...prev, movie]);
    setSearchResults([]);
    setSearchQuery('');
  };

  const removeMovie = (entityId: string) => {
    setSelectedMovies((prev) => prev.filter((m) => m.entityId !== entityId));
    setPreviewData((prev) => {
      const updated = { ...prev };
      delete updated[entityId];
      return updated;
    });
  };

  // ─── Preview ──────────────────────────────────

  const handlePreview = async (entityId: string) => {
    try {
      const res = await fetch(
        `${API_BASE}/admin/api/games/trivia/preview/${entityId}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Preview failed');
      const data = await res.json();
      setPreviewData((prev) => ({ ...prev, [entityId]: data.trivia }));
    } catch {
      notify('Failed to load trivia preview', { type: 'error' });
    }
  };

  // ─── Save ─────────────────────────────────────

  const handleSave = async () => {
    if (selectedMovies.length === 0) {
      notify('Select at least 1 movie', { type: 'warning' });
      return;
    }

    setSaving(true);
    setError('');

    try {
      const body: Record<string, unknown> = {
        gameType,
        entityType: 'movie',
        category,
        date,
        movies: selectedMovies.map((m) => ({
          entityId: m.entityId,
          displayName: m.displayName,
          releaseYear: m.releaseYear,
        })),
      };
      if (slug.trim()) body.slug = slug.trim();
      if (seoDescription.trim()) body.seo_description = seoDescription.trim();
      if (companionData?.headline) body.companion = companionData;

      const res = await fetch(`${API_BASE}/admin/api/games/trivia`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let message = 'Failed to save';
        try {
          const data = await res.json();
          message = data.message || message;
        } catch { /* non-JSON */ }
        setError(message);
        return;
      }

      notify('Trivia game config saved!', { type: 'success' });
      window.location.href = '#/game-configs';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const canSave = selectedMovies.length > 0 && !saving;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => (window.location.href = '#/game-configs')}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>
          Create Trivia Game
        </Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? 'Saving...' : 'Save Game'}
        </Button>
      </Box>

      {/* Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Game Settings</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Game Type</InputLabel>
                <Select
                  value={gameType}
                  onChange={(e) => {
                    setGameType(e.target.value);
                    setSelectedMovies([]);
                    setPreviewData({});
                  }}
                  label="Game Type"
                >
                  {GAME_TYPES.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setSelectedMovies([]);
                    setSearchResults([]);
                    setPreviewData({});
                  }}
                  label="Category"
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                label="Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {gameConfig.description}
              {gameConfig.multiMovie ? ' (can combine multiple movies)' : ' (single movie only)'}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={curatedOnly}
                  onChange={(e) => setCuratedOnly(e.target.checked)}
                  size="small"
                />
              }
              label="Curated only"
            />
          </Box>
        </CardContent>
      </Card>

      {/* SEO Fields */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            SEO (Optional — auto-generated if empty)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                fullWidth
                size="small"
                helperText="URL-friendly identifier (e.g., guess-the-movie-tamil-clues-may-10-2026)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SEO Description"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText="Search engine description (100-300 chars)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Companion Article */}
      <Box sx={{ mb: 3 }}>
        <CompanionEditor
          value={companionData}
          onChange={setCompanionData}
          gameType={gameType}
        />
      </Box>

      {/* Movie Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Select Movies</Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                label="Search movies with trivia"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                variant="outlined"
                startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={searching || !searchQuery.trim()}
                fullWidth
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Button
                variant="outlined"
                startIcon={<CasinoIcon />}
                onClick={handleRandomMovie}
                fullWidth
              >
                Random
              </Button>
            </Grid>
          </Grid>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <List dense sx={{ mt: 1 }}>
              {searchResults.map((movie) => (
                <ListItem
                  key={movie.entityId}
                  component="div"
                  onClick={() => addMovie(movie)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                >
                  <ListItemText
                    primary={movie.displayName}
                    secondary={`${movie.releaseYear} • ${movie.category}`}
                  />
                  <Chip label="Add" size="small" color="primary" variant="outlined" />
                </ListItem>
              ))}
            </List>
          )}

          {/* Selected Movies */}
          {selectedMovies.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Selected ({selectedMovies.length}):
              </Typography>
              <List dense>
                {selectedMovies.map((movie) => (
                  <ListItem key={movie.entityId}>
                    <ListItemText
                      primary={`${movie.displayName} (${movie.releaseYear})`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => handlePreview(movie.entityId)}
                        title="Preview trivia"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeMovie(movie.entityId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </CardContent>
      </Card>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Trivia Previews */}
      {Object.entries(previewData).map(([entityId, trivia]) => {
        const movie = selectedMovies.find((m) => m.entityId === entityId);
        if (!movie || !trivia) return null;

        return (
          <Card key={entityId} sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {movie.displayName} ({movie.releaseYear}) — Trivia Preview
              </Typography>

              {trivia.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {trivia.description}
                </Typography>
              )}

              {/* Show relevant preview based on game type */}
              {gameType === 'guess_the_movie' && trivia.progressive_clues && (
                <>
                  <Typography variant="subtitle2">
                    Progressive Clues ({trivia.progressive_clues.length}):
                  </Typography>
                  {trivia.progressive_clues.slice(0, 5).map((c, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 2 }}>
                      <Chip label={`W${c.weight}`} size="small" sx={{ mr: 1 }} />
                      {c.fact}
                    </Typography>
                  ))}
                  {trivia.progressive_clues.length > 5 && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      ... and {trivia.progressive_clues.length - 5} more
                    </Typography>
                  )}
                </>
              )}

              {gameType === 'redacted_plot' && trivia.redacted_plot && (
                <>
                  <Typography variant="subtitle2">
                    Plot Lines ({trivia.redacted_plot.lines?.length || 0}):
                  </Typography>
                  {trivia.redacted_plot.lines?.map((line, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
                      {i + 1}. {line}
                    </Typography>
                  ))}
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    Redacted Words ({trivia.redacted_plot.redacted_group_of_words?.length || 0}):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 2 }}>
                    {trivia.redacted_plot.redacted_group_of_words?.map((w, i) => (
                      <Chip key={i} label={w} size="small" variant="outlined" />
                    ))}
                  </Box>
                </>
              )}

              {gameType === 'fact_or_cap' && trivia.fact_or_cap && (
                <>
                  <Typography variant="subtitle2">
                    True ({trivia.fact_or_cap.true_statements?.length || 0}) / False ({trivia.fact_or_cap.false_statements?.length || 0}):
                  </Typography>
                  {trivia.fact_or_cap.true_statements?.slice(0, 3).map((s, i) => (
                    <Typography key={`t${i}`} variant="body2" sx={{ ml: 2 }}>
                      <Chip label="TRUE" size="small" color="success" sx={{ mr: 1 }} />
                      {s.statement}
                    </Typography>
                  ))}
                  {trivia.fact_or_cap.false_statements?.slice(0, 2).map((s, i) => (
                    <Typography key={`f${i}`} variant="body2" sx={{ ml: 2 }}>
                      <Chip label="FALSE" size="small" color="error" sx={{ mr: 1 }} />
                      {s.statement}
                    </Typography>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};
